import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ShoppingAddictionRecoveryStories = ({ goToInput, theme = "mint" }) => {
  // State for filtering stories
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Types of recovery
  const recoveryTypes = [
    { id: "all", label: "All Stories" },
    { id: "emotional", label: "Emotional Shoppers" },
    { id: "debt", label: "Debt Recovery" },
    { id: "mindful", label: "Mindful Spenders" },
    { id: "digital", label: "Digital Detox" }
  ];

  // Success stories data
  const successStories = [
    {
      id: 1,
      name: "Sarah J.",
      age: 34,
      location: "Denver, CO",
      categories: ["emotional", "debt"],
      before: "I shopped whenever I felt stressed or sad, maxing out three credit cards and hiding purchases from my partner.",
      journey: "After tracking my emotional triggers with Guilt Gauge for 6 months, I identified that workplace stress was my main shopping trigger.",
      after: "I've been credit card debt-free for 14 months and replaced shopping with hiking and meditation. I've saved over $12,000 since starting my recovery.",
      tip: "Track your mood before every purchase - it reveals patterns you never realized existed.",
      image: "sarah"
    },
    {
      id: 2,
      name: "Michael R.",
      age: 42,
      location: "Austin, TX",
      categories: ["digital", "mindful"],
      before: "I spent hours every night scrolling through tech sites, making 2-3 impulse purchases weekly that I rarely used.",
      journey: "The Guilt Gauge challenges helped me implement a 72-hour waiting period for all purchases over $50.",
      after: "I now research thoroughly and make just one planned tech purchase quarterly, actually using what I buy. My emergency fund is finally growing.",
      tip: "Delete shopping apps and unsubscribe from marketing emails - it's like removing temptation foods from your kitchen.",
      image: "michael"
    },
    {
      id: 3,
      name: "Aisha K.",
      age: 29,
      location: "Seattle, WA",
      categories: ["emotional", "mindful"],
      before: "I was the ultimate 'treat yourself' shopper, buying luxury items to celebrate any small win or achievement.",
      journey: "Tracking each purchase with the guilt calculator helped me see that my 'celebratory' shopping was actually causing more anxiety than joy.",
      after: "I've redefined what treating myself means - now it's experiences like concerts or cooking classes rather than accumulating more stuff.",
      tip: "For every item you want to buy, write down three ways you could celebrate without shopping.",
      image: "aisha"
    },
    {
      id: 4,
      name: "David L.",
      age: 37,
      location: "Chicago, IL",
      categories: ["debt", "digital"],
      before: "Online shopping was my evening ritual. I racked up $27,000 in debt on clothes and home goods I never used.",
      journey: "I started by deleting all shopping apps and creating a debt payoff plan. The Guilt Gauge dashboard kept me accountable.",
      after: "I'm now debt-free and have a clear system for purchases: I maintain a wishlist and only review it monthly with a strict budget.",
      tip: "Calculate how many hours you had to work to afford each purchase - it completely changes your perspective.",
      image: "david"
    },
    {
      id: 5,
      name: "Elena M.",
      age: 31,
      location: "Miami, FL",
      categories: ["emotional", "mindful"],
      before: "I shopped impulsively when bored, accumulating closets full of unused items with tags still attached.",
      journey: "Using Guilt Gauge's emotional tracking, I realized 80% of my purchases happened when I felt bored or lonely.",
      after: "I've developed a capsule wardrobe and find creative ways to style what I have instead of buying new things. I've redirected my shopping budget to my first home downpayment.",
      tip: "Take everything out of your closet and see how much you already own - it's a powerful reality check.",
      image: "elena"
    },
    {
      id: 6,
      name: "James T.",
      age: 45,
      location: "Portland, OR",
      categories: ["debt", "digital"],
      before: "I was addicted to the 'thrill of the hunt' for deals, spending hours searching for bargains I didn't need just for the dopamine hit.",
      journey: "Guilt Gauge's spending pattern analysis showed me I was spending over $400 monthly on 'bargains' that gathered dust.",
      after: "I've channeled my deal-hunting skills into optimizing our family's necessary expenses like insurance and groceries, saving us $7,300 annually.",
      tip: "A bargain isn't a bargain if you wouldn't have bought it at full price in the first place.",
      image: "james"
    }
  ];

  // Filter stories based on selected category
  const filteredStories = selectedCategory === "all" 
    ? successStories 
    : successStories.filter(story => story.categories.includes(selectedCategory));

  // Generate a visually consistent avatar for each person based on their name
  const getAvatarEmoji = (name) => {
    const emojis = ["👩‍💼", "👨‍💼", "👩‍🦱", "👨‍🦱", "👱‍♀️", "👱‍♂️", "👩", "👨"];
    // Use the name to deterministically pick an emoji
    const charSum = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
    return emojis[charSum % emojis.length];
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Shopping Addiction Recovery Stories: Real Journeys to Financial Freedom
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">You're Not Alone in Your Shopping Recovery Journey</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Read inspiring stories from people who've overcome compulsive shopping and transformed their relationship with spending.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Recovery Journey
            </motion.button>
          </div>
          <div className="text-6xl flex space-x-2">
            <span className="animate-float">🛍️</span>
            <span className="animate-float" style={{ animationDelay: "0.5s" }}>➡️</span>
            <span className="animate-float" style={{ animationDelay: "1s" }}>💰</span>
          </div>
        </div>
      </div>
      
      {/* Recovery Stats Section */}
      <section className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
        <h2 className="text-xl font-bold mb-4 dark:text-white">Shopping Addiction Recovery By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">84%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">report improved financial wellness after tracking spending</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$14,200</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual savings after recovery</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">76%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">reduction in shopping-related guilt and anxiety</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">91%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">success rate with support and tracking tools</p>
          </div>
        </div>
      </section>
      
      {/* Filter Categories */}
      <div className="mb-6 overflow-x-auto">
        <div className="flex space-x-2 min-w-max">
          {recoveryTypes.map(type => (
            <button
              key={type.id}
              onClick={() => setSelectedCategory(type.id)}
              className={`px-4 py-2 rounded-full text-sm transition ${
                selectedCategory === type.id 
                  ? `${buttonStyles[theme]} text-white` 
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Success Stories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {filteredStories.map(story => (
          <motion.div
            key={story.id}
            className="bg-gray-50 dark:bg-gray-700 rounded-xl overflow-hidden shadow-md"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="bg-white dark:bg-gray-800 w-14 h-14 rounded-full flex items-center justify-center text-3xl overflow-hidden">
                  {getAvatarEmoji(story.name)}
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-lg dark:text-white">{story.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{story.age} • {story.location}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm uppercase tracking-wider text-gray-500 dark:text-gray-400">Before</h4>
                  <p className="text-gray-700 dark:text-gray-300 italic">{story.before}</p>
                </div>
                
                <div>
                  <h4 className="text-sm uppercase tracking-wider text-gray-500 dark:text-gray-400">Recovery Journey</h4>
                  <p className="text-gray-700 dark:text-gray-300">{story.journey}</p>
                </div>
                
                <div>
                  <h4 className="text-sm uppercase tracking-wider text-gray-500 dark:text-gray-400">After</h4>
                  <p className="text-gray-700 dark:text-gray-300 font-medium">{story.after}</p>
                </div>
                
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <h4 className="text-sm uppercase tracking-wider text-gray-500 dark:text-gray-400">Recovery Tip</h4>
                  <p className="text-gray-700 dark:text-gray-300">{story.tip}</p>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Common Recovery Challenges and Solutions */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Recovery Challenges & Solutions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔁</span>
              <div>
                <h3 className="font-bold dark:text-white">Relapse After Stress</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                  Many recovered shopaholics experience urges during high-stress periods.
                </p>
                <div className="bg-white dark:bg-gray-800 p-2 rounded-lg text-sm">
                  <span className="font-semibold">Solution:</span> Create a detailed "stress response plan" with specific non-shopping coping activities.
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">👯</span>
              <div>
                <h3 className="font-bold dark:text-white">Social Pressure</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                  Friends who shop regularly can unintentionally sabotage recovery efforts.
                </p>
                <div className="bg-white dark:bg-gray-800 p-2 rounded-lg text-sm">
                  <span className="font-semibold">Solution:</span> Suggest alternative activities and be open about your recovery journey with close friends.
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📱</span>
              <div>
                <h3 className="font-bold dark:text-white">Digital Marketing</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                  Targeted ads and email promotions can trigger shopping urges.
                </p>
                <div className="bg-white dark:bg-gray-800 p-2 rounded-lg text-sm">
                  <span className="font-semibold">Solution:</span> Use ad blockers, unsubscribe from all retail emails, and use a separate browsing profile for essential shopping.
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">💔</span>
              <div>
                <h3 className="font-bold dark:text-white">Identity Shift</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                  Letting go of shopping as self-expression can create an identity void.
                </p>
                <div className="bg-white dark:bg-gray-800 p-2 rounded-lg text-sm">
                  <span className="font-semibold">Solution:</span> Explore new forms of self-expression through creativity, learning, or community involvement.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Expert Insights */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Expert Insights on Shopping Addiction Recovery</h2>
        
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6">
          <div className="flex items-start mb-4">
            <div className="bg-white dark:bg-gray-800 w-14 h-14 rounded-full flex items-center justify-center text-3xl mr-4">
              👩‍⚕️
            </div>
            <div>
              <h3 className="font-bold text-lg dark:text-white">Dr. Emily Chen</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Clinical Psychologist specializing in behavioral addictions</p>
            </div>
          </div>
          
          <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300 mb-4">
            "Shopping addiction recovery follows similar patterns to other behavioral addictions, but with unique challenges because shopping is a necessary part of life. Success comes from identifying specific emotional triggers and developing selective buying interventions rather than complete abstinence."
          </blockquote>
          
          <div className="space-y-3 text-gray-700 dark:text-gray-300">
            <p>
              Dr. Chen recommends the "SHOP" approach for sustainable recovery:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <span className="font-bold">S</span>top automatically when shopping urges appear
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <span className="font-bold">H</span>one in on your emotional state at that moment
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <span className="font-bold">O</span>ffer yourself alternative coping strategies
              </div>
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
                <span className="font-bold">P</span>lan future purchases mindfully in advance
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Recovery Resources */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Recovery Resources</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 flex flex-col h-full">
            <div className="text-center mb-3">
              <span className="text-3xl">📊</span>
              <h3 className="font-bold mt-2 dark:text-white">Tracking Tools</h3>
            </div>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-2 flex-grow">
              <li>• Guilt Gauge Spending Tracker</li>
              <li>• Shopping Journal Templates</li>
              <li>• Trigger Identification Worksheets</li>
              <li>• Progress Visualization Charts</li>
            </ul>
            <motion.button
              onClick={goToInput}
              className={`mt-4 w-full py-2 rounded-lg text-white text-sm ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Access Tools
            </motion.button>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 flex flex-col h-full">
            <div className="text-center mb-3">
              <span className="text-3xl">👥</span>
              <h3 className="font-bold mt-2 dark:text-white">Support Groups</h3>
            </div>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-2 flex-grow">
              <li>• Debtors Anonymous</li>
              <li>• Spenders Anonymous</li>
              <li>• Online Recovery Forums</li>
              <li>• Shopping Addiction Support Networks</li>
            </ul>
            <motion.button
              className={`mt-4 w-full py-2 rounded-lg text-white text-sm ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Find Support
            </motion.button>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 flex flex-col h-full">
            <div className="text-center mb-3">
              <span className="text-3xl">📚</span>
              <h3 className="font-bold mt-2 dark:text-white">Educational Resources</h3>
            </div>
            <ul className="text-gray-600 dark:text-gray-300 text-sm space-y-2 flex-grow">
              <li>• "Mind Over Money" Workbook</li>
              <li>• Psychological Triggers Guide</li>
              <li>• Financial Recovery Roadmap</li>
              <li>• Emotional Spending Webinars</li>
            </ul>
            <motion.button
              className={`mt-4 w-full py-2 rounded-lg text-white text-sm ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View Resources
            </motion.button>
          </div>
        </div>
      </section>
      
      {/* Join Our Community */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center mb-8">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Share Your Recovery Story</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Inspire others by sharing your shopping addiction recovery journey. Your story could be the motivation someone else needs.
        </p>
        <motion.button
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Submit Your Story
        </motion.button>
      </section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">About Shopping Addiction Recovery</h2>
          <p className="mb-3">
            Shopping addiction recovery is the journey from compulsive buying behavior to mindful, intentional spending habits. It involves understanding emotional triggers, developing healthy coping mechanisms, and creating sustainable financial practices.
          </p>
          <p className="mb-3">
            The recovery stories shared on this page represent real experiences from people who have overcome shopping addiction. While each journey is unique, common themes include identifying emotional triggers, implementing practical strategies like delayed purchasing, and finding support through communities and tools like Guilt Gauge.
          </p>
          <p>
            If you're struggling with compulsive shopping, remember that recovery is possible. The testimonials on this page demonstrate that with the right strategies and support, you can transform your relationship with shopping and build a healthier financial future.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default ShoppingAddictionRecoveryStories;
